import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Download,
  TrendingUp,
  DollarSign,
  Users,
  Package
} from 'lucide-react'
import { useAuth } from '../App'
import { AgentAPI, Allocation } from '../services/api'
import Header from '../components/Header'
import BottomNav from '../components/BottomNav'

const Reports: React.FC = () => {
  const { user, logout } = useAuth()
  const [allocations, setAllocations] = useState<Allocation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const data = await AgentAPI.getMyAllocations()
        setAllocations(data)
      } catch (err) {
        console.error('Failed to load allocations:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  // Calculate aggregated stats
  const stats = {
    totalAllocated: allocations.reduce((sum, a) => sum + a.allocated_qty, 0),
    totalSold: allocations.reduce((sum, a) => sum + a.sold_qty, 0),
    totalRevenue: allocations.reduce((sum, a) => sum + a.advance_received, 0),
    totalBalance: allocations.reduce((sum, a) => sum + a.balance_due, 0),
    activeSeries: allocations.filter(a => a.status === 'active').length,
  }

  const conversionRate = stats.totalAllocated > 0 
    ? ((stats.totalSold / stats.totalAllocated) * 100).toFixed(1)
    : '0.0'

  return (
    <div className="min-h-screen bg-gray-100 pb-safe">
      <Header title="Reports & Analytics" showLogout onLogout={logout} />

      <div className="p-4 space-y-4">
        {/* Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-4 text-white"
        >
          <p className="text-primary-100 text-sm">Agency</p>
          <h2 className="text-xl font-bold">{user?.agencyName}</h2>
          <p className="text-primary-200 text-sm mt-1">{user?.name}</p>
        </motion.div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-3">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-gray-500">Conversion Rate</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{conversionRate}%</p>
            <p className="text-xs text-gray-500 mt-1">{stats.totalSold} / {stats.totalAllocated} sold</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-5 h-5 text-purple-600" />
              <p className="text-sm text-gray-500">Active Series</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.activeSeries}</p>
            <p className="text-xs text-gray-500 mt-1">out of {allocations.length} total</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <p className="text-sm text-gray-500">Revenue Collected</p>
            </div>
            <p className="text-xl font-bold text-green-600">PKR {stats.totalRevenue.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">Advance received</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-amber-600" />
              <p className="text-sm text-gray-500">Balance Due</p>
            </div>
            <p className="text-xl font-bold text-amber-600">PKR {stats.totalBalance.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">Outstanding</p>
          </motion.div>
        </div>

        {/* Allocation Breakdown */}
        <div className="bg-white rounded-xl p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Allocation Breakdown</h3>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
          ) : allocations.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No allocations yet</p>
          ) : (
            <div className="space-y-3">
              {allocations.map((alloc, index) => (
                <motion.div
                  key={alloc.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b border-gray-100 last:border-0 pb-3 last:pb-0"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">
                        {alloc.series_name || `Series #${alloc.series_id}`}
                      </p>
                      <p className="text-xs text-gray-500">{alloc.series_code}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      alloc.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {alloc.status}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Sold: {alloc.sold_qty}/{alloc.allocated_qty}</span>
                    <span className="text-green-600">PKR {alloc.advance_received.toLocaleString()}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Export Button (Placeholder) */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="w-full flex items-center justify-center gap-2 py-3 bg-primary-500 text-white rounded-xl font-medium"
          onClick={() => alert('Export feature coming soon')}
        >
          <Download size={20} />
          Export Report (Coming Soon)
        </motion.button>
      </div>

      <BottomNav activeTab="reports" />
    </div>
  )
}

export default Reports

